import { useCallback, useEffect, useMemo, useState } from "react";
import { localizeTextWithNumbers } from "src/utils/localizeTextWithNumbers";

const CACHE_STORAGE_KEY = "dynamic_arabic_translations_v1";
const DEFAULT_TRANSLATION_ENDPOINT = process.env.REACT_APP_TRANSLATION_API_URL || "";
const TRANSLATION_API_KEY = process.env.REACT_APP_TRANSLATION_API_KEY || "";

const inMemoryCache = new Map();
const inflightRequests = new Map();
let storageLoaded = false;

const isArabicLanguage = language =>
  typeof language === "string" && language.toLowerCase().startsWith("ar");

const hasArabicChars = value => /[\u0600-\u06FF]/.test(value);

const normalizeInput = value => String(value || "").trim();

const getCacheKey = (language, text) =>
  `${isArabicLanguage(language) ? "ar" : "default"}::${normalizeInput(text).toLowerCase()}`;

const loadCacheFromStorage = () => {
  if (storageLoaded || typeof window === "undefined") {
    return;
  }
  storageLoaded = true;
  try {
    const raw = window.localStorage.getItem(CACHE_STORAGE_KEY);
    if (!raw) {
      return;
    }
    const parsed = JSON.parse(raw);
    Object.entries(parsed).forEach(([key, value]) => {
      if (typeof value === "string" && value.trim()) {
        inMemoryCache.set(key, value);
      }
    });
  } catch {
    // Ignore cache parse issues and continue with in-memory cache.
  }
};

const persistCacheToStorage = () => {
  if (typeof window === "undefined") {
    return;
  }
  try {
    const serializable = {};
    inMemoryCache.forEach((value, key) => {
      serializable[key] = value;
    });
    window.localStorage.setItem(CACHE_STORAGE_KEY, JSON.stringify(serializable));
  } catch {
    // Best effort only.
  }
};

const parseTranslatedValue = payload =>
  payload?.translatedText ||
  payload?.translation ||
  payload?.data?.translatedText ||
  payload?.result ||
  "";

const fetchArabicTranslation = async text => {
  if (!DEFAULT_TRANSLATION_ENDPOINT) {
    return "";
  }

  const headers = {
    "Content-Type": "application/json",
  };
  if (TRANSLATION_API_KEY) {
    headers.Authorization = `Bearer ${TRANSLATION_API_KEY}`;
  }

  const response = await fetch(DEFAULT_TRANSLATION_ENDPOINT, {
    method: "POST",
    headers,
    body: JSON.stringify({
      text,
      q: text,
      source: "en",
      target: "ar",
      target_language: "ar",
      format: "text",
    }),
  });

  if (!response.ok) {
    return "";
  }
  const data = await response.json().catch(() => ({}));
  return parseTranslatedValue(data);
};

export const translateDynamicText = async (text, language) => {
  loadCacheFromStorage();

  const original = normalizeInput(text);
  if (!original) {
    return "";
  }

  const baseLocalized = localizeTextWithNumbers(original, language);
  if (!isArabicLanguage(language) || hasArabicChars(original)) {
    return baseLocalized;
  }

  const cacheKey = getCacheKey(language, original);
  if (inMemoryCache.has(cacheKey)) {
    return localizeTextWithNumbers(inMemoryCache.get(cacheKey), language);
  }

  if (!inflightRequests.has(cacheKey)) {
    const request = fetchArabicTranslation(original)
      .then(translated => {
        const finalText = normalizeInput(translated) || original;
        inMemoryCache.set(cacheKey, finalText);
        persistCacheToStorage();
        return finalText;
      })
      .catch(() => original)
      .finally(() => {
        inflightRequests.delete(cacheKey);
      });
    inflightRequests.set(cacheKey, request);
  }

  const translatedValue = await inflightRequests.get(cacheKey);
  return localizeTextWithNumbers(translatedValue || original, language);
};

export const useDynamicTextTranslator = (texts, language) => {
  const [refreshToken, setRefreshToken] = useState(0);

  const textsKey = Array.isArray(texts)
    ? texts.map(normalizeInput).filter(Boolean).join("\u0000")
    : "";

  const normalizedTexts = useMemo(
    () => (textsKey ? Array.from(new Set(textsKey.split("\u0000"))) : []),
    [textsKey]
  );

  useEffect(() => {
    if (!isArabicLanguage(language) || normalizedTexts.length === 0) {
      return;
    }
    const uncached = normalizedTexts.filter(
      value => !inMemoryCache.has(getCacheKey(language, value))
    );
    if (uncached.length === 0) {
      return;
    }

    let isMounted = true;

    Promise.all(uncached.map(value => translateDynamicText(value, language)))
      .then(() => {
        if (isMounted) {
          setRefreshToken(prev => prev + 1);
        }
      })
      .catch(() => {
        // Keep fallback text if translation fails.
      });

    return () => {
      isMounted = false;
    };
  }, [language, normalizedTexts]);

  return useCallback(
    text => {
      void refreshToken;
      const normalized = normalizeInput(text);
      if (!normalized) {
        return "";
      }
      if (!isArabicLanguage(language)) {
        return localizeTextWithNumbers(normalized, language);
      }

      const cacheKey = getCacheKey(language, normalized);
      const cached = inMemoryCache.get(cacheKey);
      if (cached) {
        return localizeTextWithNumbers(cached, language);
      }

      translateDynamicText(normalized, language).then(() => {
        setRefreshToken(prev => prev + 1);
      });

      return localizeTextWithNumbers(normalized, language);
    },
    [language, refreshToken]
  );
};
